import {
  CpsUiAdminConfigType,
  CpsUiAdminConfigState,
  CpsHelpCenterState,
  CPS_UI_ADMIN_CONFIG_TYPE,
  CpsHelpCenterItemState
} from '@cs-cms/types';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalizationContext } from '../contexts/GlobalizationContext';
import { UiAdminConfigsContext } from '../contexts/UiAdminConfigsContext';
import { useTranslation } from '../hooks/globalization/useTranslation';
import { useGetUiAdminConfigEntry } from '../hooks/uiAdminConfigs/useGetUiAdminConfigEntry';
import { useGetUiAdminConfigWritePermissions } from '../hooks/uiAdminConfigs/useGetUiAdminConfigWritePermissions';
import { useUpsertUiAdminConfigEntry } from '../hooks/uiAdminConfigs/useUpsertUiAdminConfigEntry';
import { useNotificationContext } from '../hooks/useNotificationContext';

interface UiAdminConfigsProviderProps {
  children?: React.ReactNode;
}

export const UiAdminConfigsProvider: React.FC<UiAdminConfigsProviderProps> = ({ children }) => {
  const [configKey, setConfigKey] = React.useState<CpsUiAdminConfigType>();
  const [configWritePermissionsKeys, setConfigWritePermissionsKeys] = React.useState<CpsUiAdminConfigType[]>();
  const [uiAdminConfigState, setUiAdminConfigState] = React.useState<CpsUiAdminConfigState>({});
  const [tempHelpCenterState, setTempHelpCenterState] = React.useState<Partial<CpsHelpCenterState>>();
  const initializedKeys = React.useRef<Set<CpsUiAdminConfigType>>(new Set());
  const {
    data: uiAdminConfigEntry,
    isLoading: isLoadingGetUiAdminConfigEntry,
    isFetched: isFetchedGetUiAdminConfigEntry
  } = useGetUiAdminConfigEntry(configKey);
  const { data: uiAdminConfigWritePermissions, isLoading: isLoadingUiAdminConfigWritePermissions } =
    useGetUiAdminConfigWritePermissions(configWritePermissionsKeys);
  const { mutate: mutateUpsertUiAdminConfig, isPending: isLoadingMutateUpsertUiConfigEntry } =
    useUpsertUiAdminConfigEntry();
  const { showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { pathname } = useLocation();

  const refreshUiAdminConfigState = (newConfigKey: CpsUiAdminConfigType) => {
    setConfigKey(newConfigKey);
  };

  const handleUpsertUiAdminConfigEntry = (key: CpsUiAdminConfigType, body: string): void => {
    if (key !== configKey) {
      setConfigKey(key);
    }
    mutateUpsertUiAdminConfig(
      { key, body },
      {
        onSuccess: () => {
          let messageKey = '';
          switch (key) {
            case CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR:
              messageKey = 'custom.notifications.messageBarSaved';
              break;
            case CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE:
              messageKey = 'custom.notifications.maintenancePageSaved';
              break;
            case CPS_UI_ADMIN_CONFIG_TYPE.HELP_CENTER:
              messageKey = 'custom.notifications.helpCenterSaved';
              break;
            case CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE:
              messageKey = 'custom.notifications.reportIssueSaved';
              break;
            default:
              break;
          }
          showToast({
            type: 'success',
            message: translate(messageKey)
          });

          initializedKeys.current.delete(key);
          refreshUiAdminConfigState(key);
        }
      }
    );
  };

  const [uiAdminConfigPreviewState, setUiAdminConfigPreviewState] = React.useState<CpsUiAdminConfigState>({
    [CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: undefined,
    [CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined,
    [CPS_UI_ADMIN_CONFIG_TYPE.HELP_CENTER]: undefined,
    [CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined,
    [CPS_UI_ADMIN_CONFIG_TYPE.ABOUT]: undefined
  });

  const resetPreviewState = () => {
    setUiAdminConfigPreviewState({
      [CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.HELP_CENTER]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.ABOUT]: undefined
    });
  };

  React.useEffect(() => {
    const keysToFetch = [
      CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE,
      CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR,
      CPS_UI_ADMIN_CONFIG_TYPE.HELP_CENTER,
      CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE,
      CPS_UI_ADMIN_CONFIG_TYPE.ABOUT
    ];

    for (const key of keysToFetch) {
      if (!initializedKeys.current.has(key) && !(key in uiAdminConfigState)) {
        setConfigKey(key);
        return;
      }
    }
  }, [uiAdminConfigState]);

  React.useEffect(() => {
    if (!configKey || !setUiAdminConfigState || !isFetchedGetUiAdminConfigEntry) {
      return;
    }

    if (initializedKeys.current.has(configKey)) {
      return;
    }

    let parsedValue: any;
    if (uiAdminConfigEntry) {
      if (configKey === CPS_UI_ADMIN_CONFIG_TYPE.ABOUT) {
        parsedValue = uiAdminConfigEntry;
      } else {
        try {
          parsedValue = JSON.parse(uiAdminConfigEntry);
        } catch {
          parsedValue = uiAdminConfigEntry;
        }
      }
    } else {
      parsedValue = {};
    }

    initializedKeys.current.add(configKey);

    setUiAdminConfigState(prevState => ({
      ...prevState,
      [configKey]: parsedValue
    }));
  }, [configKey, isFetchedGetUiAdminConfigEntry, uiAdminConfigEntry, uiAdminConfigState]);

  React.useEffect(() => {
    // reset preview State on route change
    setUiAdminConfigPreviewState({
      [CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.HELP_CENTER]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined,
      [CPS_UI_ADMIN_CONFIG_TYPE.ABOUT]: undefined
    });

    if (uiAdminConfigState.helpCenter) {
      const items: CpsHelpCenterItemState[] = (uiAdminConfigState.helpCenter as CpsHelpCenterState).items;
      setTempHelpCenterState({ items });
    } else {
      setTempHelpCenterState({});
    }
  }, [pathname, uiAdminConfigState.helpCenter]);

  React.useEffect(() => {
    if (
      isLoadingGetUiAdminConfigEntry ||
      isLoadingMutateUpsertUiConfigEntry ||
      isLoadingUiAdminConfigWritePermissions
    ) {
      return;
    }

    if (tempHelpCenterState !== undefined) {
      return;
    }

    if (uiAdminConfigState.helpCenter) {
      const items: CpsHelpCenterItemState[] = (uiAdminConfigState.helpCenter as CpsHelpCenterState).items;
      setTempHelpCenterState({ items });
    } else {
      setTempHelpCenterState({});
    }
  }, [
    isLoadingGetUiAdminConfigEntry,
    isLoadingMutateUpsertUiConfigEntry,
    isLoadingUiAdminConfigWritePermissions,
    tempHelpCenterState,
    uiAdminConfigState.helpCenter
  ]);

  return (
    <UiAdminConfigsContext.Provider
      value={{
        isLoading:
          isLoadingGetUiAdminConfigEntry ||
          isLoadingMutateUpsertUiConfigEntry ||
          isLoadingUiAdminConfigWritePermissions,
        upsertUiAdminConfigEntry: handleUpsertUiAdminConfigEntry,
        setConfigWritePermissionsKeys,
        uiAdminConfigWritePermissions,
        uiAdminConfigPreviewState,
        setUiAdminConfigPreviewState,
        resetPreviewState,
        uiAdminConfigState,
        refreshUiAdminConfigState,
        tempHelpCenterState,
        setTempHelpCenterState
      }}
    >
      {children}
    </UiAdminConfigsContext.Provider>
  );
};
